<template>
    <v-dialog v-model="dialog" :max-width="dialogMaxWidth" lazy-validation persistent>
        <template v-slot:activator="{ on, attrs }">
            <slot name="activator" :on="on" :attrs="attrs" :open="openDialog">
                <v-btn color="primary" class="mr-2" :disabled="disabled" v-bind="attrs" v-on="on" @click="openDialog">
                    <v-icon left>mdi-connection</v-icon>
                    <span>Storage</span>
                </v-btn>
            </slot>
        </template>

        <v-card>
            <v-card-title class="flex-nowrap align-start">
                Attach storage
                <v-spacer></v-spacer>
                <v-btn @click="dialog = false" icon><v-icon>clear</v-icon></v-btn>
            </v-card-title>
            <v-card-text>
                <v-form v-model="mountFormIsValid" ref="mountCreateForm">
                    <div class="d-flex align-start">
                        <v-select
                            v-model="selectedStorageOption"
                            :items="storageOptions"
                            label="Select a storage option"
                            item-text="long_id"
                            item-value="afsid"
                            outlined
                            dense
                            class="flex-grow-1"
                            :rules="[rules.required]">
                            <template v-slot:item="{ item, on, attrs }">
                                <v-list-item two-line v-bind="attrs" v-on="on">
                                    <v-list-item-content>
                                        <v-list-item-title>{{ item.long_id }}</v-list-item-title>
                                        <v-list-item-subtitle>
                                            {{ item.quota_gib }} GiB
                                            <strong v-if="item.force_read_only">read only</strong>
                                            <span v-if="item.cancellation_timestamp">
                                                <br />
                                                Available until {{ item.last_availability_date | dateToHuman }}
                                            </span>
                                        </v-list-item-subtitle>
                                    </v-list-item-content>
                                </v-list-item>
                            </template>
                        </v-select>
                        <v-btn text color="primary" class="ml-2" :to="createStorageRoute" @click="dialog = false">
                            <v-icon left>mdi-plus</v-icon>
                            Storage
                        </v-btn>
                    </div>
                    <v-alert type="warning" text dense class="mt-3" v-if="alreadyMountedUnder">
                        This storage is already attached under
                        <code>{{ alreadyMountedUnder }}</code>
                    </v-alert>
                    <v-text-field v-model="mountPoint" outlined hint="This field is required" :rules="[rules.required, rules.validChars, rules.unique]" dense>
                        <template v-slot:label>Path</template>
                    </v-text-field>
                    <div class="mb-1">Storage will be accessible under</div>
                    <code class="d-flex rounded py-2 px-3">
                        {{ mountBasePath }}/
                        <b>{{ mountPoint }}</b>
                    </code>
                    <v-checkbox v-model="mountReadOnly" hide-details :disabled="selectedStorageOptionObject?.force_read_only">
                        <template v-slot:label>Read only mode</template>
                    </v-checkbox>
                </v-form>
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn text color="primary" @click="sendPostRequest()" :disabled="!mountFormIsValid" :loading="addingMount">Attach</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { sortBy } from 'lodash'
import { mapState } from 'vuex'
import { createBackendErrorHandler } from '@/utils/errorMessages'
import * as filesStorageErrors from '@/modules/files/errors'
import * as integrationsErrors from '@/modules/user/errors/integrations'
import * as lifecycleErrors from '@/modules/snapshot/errors/lifecycle'
import * as permissionsErrors from '@/utils/errors/permissions'
import * as spaceErrors from '@/modules/space/errors'

const { getBackendErrorMessage } = createBackendErrorHandler(filesStorageErrors, integrationsErrors, lifecycleErrors, permissionsErrors, spaceErrors)

export default {
    name: 'SpaceAttachStorageDialog',
    props: {
        spaceId: {
            type: [String, Number],
            default: null
        },
        disabled: {
            type: Boolean,
            default: false
        },
        dialogMaxWidth: {
            type: String,
            default: '640px'
        }
    },
    data() {
        return {
            dialog: false,
            storageOptions: [],
            mountedStorageList: [],
            selectedStorageOption: null,
            mountFormIsValid: true,
            mountPoint: '',
            mountReadOnly: false,
            addingMount: false,
            rules: {
                required: value => !!value || 'This field is required',
                validChars: p => /^[a-zA-Z0-9-_]*$/g.test(p) || 'Please use only letters, numbers, underscores and dashes',
                unique: value => !this.mountPoints.includes(value) || 'A storage is already mounted on that path'
            }
        }
    },
    computed: {
        ...mapState('userStore', ['selectedResourcePool']),
        ...mapState('spaceStore', ['mappedResources']),
        resolvedSpaceId() {
            return this.spaceId || this.$route.params.sid
        },
        selectedStorageOptionObject() {
            return this.storageOptions.find(option => option.afsid === this.selectedStorageOption)
        },
        mountBasePath() {
            return this.storageOptions[0]?.base_path
        },
        alreadyMountedUnder() {
            if (!this.selectedStorageOptionObject) {
                return false
            }
            const { afsid } = this.selectedStorageOptionObject
            const mountPath = this.mountedStorageList.find(storage => storage.afsid === afsid)?.mount_path
            return mountPath
        },
        reportingResourcePoolId() {
            const rpidFromRoute = Number(this.$route.params.rpid)
            if (Number.isFinite(rpidFromRoute) && rpidFromRoute > 0) {
                return rpidFromRoute
            }

            const rpidFromStore = Number(this.selectedResourcePool)
            if (Number.isFinite(rpidFromStore) && rpidFromStore > 0) {
                return rpidFromStore
            }

            return null
        },
        lfsMappedResourcePoolId() {
            const lfsResourcePoolId = Object.keys(this.mappedResources?.LFS || {})[0]
            const mappedResourcePoolId = Number(lfsResourcePoolId)

            if (Number.isFinite(mappedResourcePoolId) && mappedResourcePoolId > 0) {
                return mappedResourcePoolId
            }

            return null
        },
        createStorageRoute() {
            const resourcePoolId = this.lfsMappedResourcePoolId || this.reportingResourcePoolId

            if (resourcePoolId) {
                return {
                    name: 'reporting-rp-overview',
                    params: { rpid: resourcePoolId },
                    hash: '#large-file-storage'
                }
            }

            return {
                name: 'reporting-dashboard'
            }
        },
        mountPoints() {
            return this.mountedStorageList.map(mount => this.trimMountPath(mount.mount_path))
        }
    },
    methods: {
        async openDialog() {
            if (this.resolvedSpaceId) {
                try {
                    await this.$store.dispatch('spaceStore/fetchMappedResources', this.resolvedSpaceId)
                } catch (error) {}
            }
            await this.fetchStorageOptions()
            await this.fetchMountedStorageList()
            this.dialog = true
        },
        async fetchStorageOptions() {
            if (!this.resolvedSpaceId) {
                return
            }
            const endpoint = `/spaces/${this.resolvedSpaceId}/afs`
            try {
                const { data } = await this.$axios.get(endpoint)
                this.storageOptions = sortBy(data, 'long_id')
            } catch (error) {
                const snackBarText = getBackendErrorMessage(error, error.response?.data?.debug || 'There was an error fetching available storage infromation')
                if (snackBarText) {
                    this.$store.dispatch('showSnackBar', {
                        snackBarText,
                        snackBarTimeout: 5000,
                        snackBarIcon: 'error'
                    })
                }
            }
        },
        async fetchMountedStorageList() {
            if (!this.resolvedSpaceId) {
                this.mountedStorageList = []
                return
            }
            const endpoint = `/spaces/${this.resolvedSpaceId}/mounts`
            try {
                const { data } = await this.$axios.get(endpoint)
                this.mountedStorageList = data
            } catch (error) {
                const snackBarText = getBackendErrorMessage(error, error.response?.data?.debug || 'There was an error fetching mounted storage infromation')
                if (snackBarText) {
                    this.$store.dispatch('showSnackBar', {
                        snackBarText,
                        snackBarTimeout: 5000,
                        snackBarIcon: 'error'
                    })
                }
            }
        },
        async sendPostRequest() {
            if (!this.resolvedSpaceId) {
                return
            }
            this.addingMount = true
            const endpoint = `/spaces/${this.resolvedSpaceId}/mounts`
            const body = {
                afsid: this.selectedStorageOption,
                mount_path: this.mountPoint,
                read_only: this.mountReadOnly
            }
            try {
                await this.$axios.post(endpoint, body)
                this.$emit('attached')
            } catch (error) {
                const snackBarText = getBackendErrorMessage(error, 'There was an error mounting storage')
                if (snackBarText) {
                    this.$store.dispatch('showSnackBar', {
                        snackBarText,
                        snackBarTimeout: 5000,
                        snackBarIcon: 'error'
                    })
                }
            }
            this.addingMount = false
            this.dialog = false
            this.mountPoint = ''
            this.selectedStorageOption = null
            this.$refs.mountCreateForm.reset()
            this.mountReadOnly = false
        },
        trimMountPath(path) {
            return path.replace(`${this.mountBasePath}/`, '')
        }
    },
    watch: {
        selectedStorageOptionObject(to) {
            if (to?.force_read_only) {
                this.mountReadOnly = true
            }
        }
    }
}
</script>
